import { initializeApp, deleteApp } from 'firebase/app';
import {
  createUserWithEmailAndPassword,
  deleteUser,
  getAuth,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { app, auth, db } from '../../config/firebase';
import { friendlyError } from '../../utils/userMessages';

const ADMIN_RUNTIME = doc(db, 'systemSettings', 'admin_runtime');
const PUBLIC_RUNTIME = doc(db, 'systemSettings', 'public_runtime');
const PLATFORM_CONFIG = doc(db, 'systemConfig', 'platform');
const SECRET_DOC = doc(db, 'platformPrivateSettings', 'integrations');
const TEMPLATE_COLLECTION = 'platformEmailTemplates';

const normalizeRole = (role) => String(role || '').toLowerCase().replace(/[\s-]+/g, '_');
const isSuperAdminRole = (role) => ['super_admin', 'superadmin'].includes(normalizeRole(role));

const DEFAULT_TEMPLATES = {
  account_verification: { name: 'Account verification', subject: 'Verify your TELCOFAIR account', html: '<p>Hello {{firstName}},</p><p>Verify your account: <a href="{{verificationLink}}">Verify account</a></p>', text: 'Hello {{firstName}}, verify your account: {{verificationLink}}' },
  verification_resend: { name: 'Verification resend', subject: 'Your TELCOFAIR verification link', html: '<p>Hello {{firstName}},</p><p>Your new verification link is <a href="{{verificationLink}}">here</a>.</p>', text: 'Verification link: {{verificationLink}}' },
  welcome: { name: 'Welcome', subject: 'Welcome to TELCOFAIR', html: '<p>Welcome {{firstName}}.</p>', text: 'Welcome {{firstName}}.' },
  registration_completion: { name: 'Registration completion reminder', subject: 'Complete your TELCOFAIR registration', html: '<p>Hello {{firstName}},</p><p>Your registration is not complete. <a href="{{registrationLink}}">Continue registration</a>.</p>', text: 'Continue registration: {{registrationLink}}' },
  registration_expired: { name: 'Registration expired', subject: 'Your TELCOFAIR registration needs attention', html: '<p>Hello {{firstName}}, your registration window has expired. Please return to TELCOFAIR to continue.</p>', text: 'Your TELCOFAIR registration needs attention.' },
  password_reset: { name: 'Password reset', subject: 'Reset your TELCOFAIR password', html: '<p>Reset your password: <a href="{{resetLink}}">Reset password</a></p>', text: 'Reset password: {{resetLink}}' },
  password_changed: { name: 'Password changed', subject: 'Your TELCOFAIR password changed', html: '<p>Your password was changed.</p>', text: 'Your password was changed.' },
  company_registration_completed: { name: 'Company registration completed', subject: 'Company registration completed', html: '<p>{{companyName}} registration is complete.</p>', text: '{{companyName}} registration is complete.' },
  registration_payment_required: { name: 'Registration payment required', subject: 'Registration payment required', html: '<p>A payment of {{amount}} is required to complete registration.</p>', text: 'Registration payment required: {{amount}}' },
  payment_success: { name: 'Payment success', subject: 'Payment received', html: '<p>Your payment of {{amount}} was received.</p>', text: 'Payment received: {{amount}}' },
  payment_approved_receipt: { name: 'Payment approved + receipt', subject: 'Payment approved · {{receiptNumber}}', html: '<p>Hello {{firstName}},</p><p>Your payment of <strong>{{amount}}</strong> for {{planName}} has been approved.</p><p>Receipt: <strong>{{receiptNumber}}</strong></p><p>{{message}}</p>', text: 'Your payment of {{amount}} for {{planName}} has been approved. Receipt: {{receiptNumber}}. {{message}}' },
  payment_rejected: { name: 'Payment rejected', subject: 'Payment verification needs attention', html: '<p>Hello {{firstName}},</p><p>Your payment could not be approved.</p><p>Reason: {{message}}</p><p>Please return to TELCOFAIR and submit corrected payment proof.</p>', text: 'Your payment could not be approved. Reason: {{message}}' },
  payment_failed: { name: 'Payment failed', subject: 'Payment needs attention', html: '<p>Your payment could not be completed.</p>', text: 'Your payment could not be completed.' },
  subscription_activated: { name: 'Subscription activated', subject: '{{planName}} activated', html: '<p>Your {{planName}} subscription is active.</p>', text: '{{planName}} subscription is active.' },
  subscription_expiring: { name: 'Subscription expiring', subject: 'Your TELCOFAIR subscription is expiring', html: '<p>Your subscription is approaching its renewal date.</p>', text: 'Your subscription is approaching its renewal date.' },
  subscription_expired: { name: 'Subscription expired', subject: 'Your TELCOFAIR subscription expired', html: '<p>Your subscription has expired.</p>', text: 'Your subscription has expired.' },
  team_invitation: { name: 'Team invitation', subject: 'You have been invited to TELCOFAIR', html: '<p>You have been invited to join {{companyName}}.</p>', text: 'You have been invited to join {{companyName}}.' },
  team_member_created: { name: 'Team member created', subject: 'Your TELCOFAIR team account', html: '<p>Your team account is ready.</p>', text: 'Your team account is ready.' },
  meeting_scheduled: { name: 'Meeting scheduled', subject: 'Meeting scheduled: {{meetingTitle}}', html: '<p>{{meetingTitle}} is scheduled for {{meetingDate}}.</p>', text: '{{meetingTitle}} is scheduled for {{meetingDate}}.' },
  meeting_rescheduled: { name: 'Meeting rescheduled', subject: 'Meeting rescheduled: {{meetingTitle}}', html: '<p>{{meetingTitle}} has been rescheduled.</p>', text: '{{meetingTitle}} has been rescheduled.' },
  meeting_cancelled: { name: 'Meeting cancelled', subject: 'Meeting cancelled: {{meetingTitle}}', html: '<p>{{meetingTitle}} has been cancelled.</p>', text: '{{meetingTitle}} has been cancelled.' },
  meeting_reminder: { name: 'Meeting reminder', subject: 'Meeting reminder: {{meetingTitle}}', html: '<p>Reminder: {{meetingTitle}} begins {{meetingDate}}.</p>', text: 'Reminder: {{meetingTitle}} begins {{meetingDate}}.' },
  new_message: { name: 'New message', subject: 'New TELCOFAIR message', html: '<p>You have a new message.</p>', text: 'You have a new message.' },
  security_alert: { name: 'Security alert', subject: 'TELCOFAIR security alert', html: '<p>A security-related event occurred on your account.</p>', text: 'A security-related event occurred on your account.' },
  system_notification: { name: 'System notification', subject: 'TELCOFAIR notification', html: '<p>{{message}}</p>', text: '{{message}}' },
};

const SENSITIVE_SETTING_KEYS = new Set([
  'password', 'pass', 'apikey', 'apitoken', 'token', 'authtoken', 'authkey',
  'secret', 'secretkey', 'webhooksecret', 'appcertificate', 'clientsecret',
  'privatekey', 'accesskey', 'secretaccesskey',
]);
const normalizeSensitiveKey = (key) => String(key || '').replace(/[^a-z0-9]/gi, '').toLowerCase();
const isSensitiveSettingKey = (key) => SENSITIVE_SETTING_KEYS.has(normalizeSensitiveKey(key));

const omitSensitiveSettings = (value) => {
  if (Array.isArray(value)) return value.map(omitSensitiveSettings);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(Object.entries(value)
    .filter(([key]) => !isSensitiveSettingKey(key))
    .map(([key, item]) => [key, omitSensitiveSettings(item)]));
};

const stripUndefined = (value) => {
  if (Array.isArray(value)) return value.map(stripUndefined);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined).map(([k, v]) => [k, stripUndefined(v)]));
};

const deepMerge = (base, extra) => {
  const out = { ...(base || {}) };
  if (!extra || typeof extra !== 'object' || Array.isArray(extra)) return out;
  Object.entries(extra).forEach(([key, value]) => {
    out[key] = value && typeof value === 'object' && !Array.isArray(value) ? deepMerge(out[key] || {}, value) : value;
  });
  return out;
};

const publicProjection = (settings = {}) => ({
  auth: {
    provider: settings.auth?.provider || 'firebase_auth',
    verificationProvider: settings.auth?.verificationProvider || 'firebase_auth',
    passwordResetProvider: settings.auth?.passwordResetProvider || 'firebase_auth',
  },
  registration: {
    paymentRequired: Boolean(settings.registration?.paymentRequired),
  },
  email: {
    enabled: Boolean(settings.email?.enabled),
    provider: settings.email?.provider || 'smtp',
    gatewayUrl: settings.email?.gatewayUrl || '',
    verificationGatewayUrl: settings.email?.verificationGatewayUrl || settings.email?.gatewayUrl || '',
    passwordResetGatewayUrl: settings.email?.passwordResetGatewayUrl || settings.email?.gatewayUrl || '',
    verificationBaseUrl: settings.email?.verificationBaseUrl || '',
    fromName: settings.email?.fromName || 'TELCOFAIR',
    fromEmail: settings.email?.fromEmail || '',
  },
  notifications: {
    inApp: settings.notifications?.inApp !== false,
    email: Boolean(settings.notifications?.email),
    firebasePush: Boolean(settings.notifications?.firebasePush),
    sms: Boolean(settings.notifications?.sms),
    whatsapp: Boolean(settings.notifications?.whatsapp),
  },
  sms: {
    enabled: Boolean(settings.sms?.enabled),
    provider: settings.sms?.provider || 'custom',
    gatewayUrl: settings.sms?.provider === 'custom' ? (settings.sms?.custom?.endpoint || '') : '',
    method: settings.sms?.custom?.method || 'POST',
    senderId: settings.sms?.custom?.senderId || '',
    phoneField: settings.sms?.custom?.phoneField || 'phone',
    messageField: settings.sms?.custom?.messageField || 'message',
  },
  payments: {
    enabled: Boolean(settings.payments?.enabled),
    provider: settings.payments?.provider || 'stripe',
    currency: settings.payments?.currency || 'EUR',
    online: { enabled: settings.payments?.online?.enabled !== false },
    stripePublishableKey: settings.payments?.stripe?.publishableKey || '',
    revolutPublicKey: settings.payments?.revolut?.publicKey || '',
    manual: {
      approvalRequired: settings.payments?.manual?.approvalRequired !== false,
      upi: {
        enabled: Boolean(settings.payments?.manual?.upi?.enabled),
        upiId: settings.payments?.manual?.upi?.upiId || '',
        payeeName: settings.payments?.manual?.upi?.payeeName || '',
        qrUrl: settings.payments?.manual?.upi?.qrUrl || '',
        instructions: settings.payments?.manual?.upi?.instructions || '',
      },
      bankTransfer: {
        enabled: Boolean(settings.payments?.manual?.bankTransfer?.enabled),
        accountHolder: settings.payments?.manual?.bankTransfer?.accountHolder || '',
        bankName: settings.payments?.manual?.bankTransfer?.bankName || '',
        accountNumber: settings.payments?.manual?.bankTransfer?.accountNumber || '',
        ifsc: settings.payments?.manual?.bankTransfer?.ifsc || '',
        branch: settings.payments?.manual?.bankTransfer?.branch || '',
        swift: settings.payments?.manual?.bankTransfer?.swift || '',
        iban: settings.payments?.manual?.bankTransfer?.iban || '',
        instructions: settings.payments?.manual?.bankTransfer?.instructions || '',
      },
    },
    receipt: {
      enabled: settings.payments?.receipt?.enabled !== false,
      title: settings.payments?.receipt?.title || 'TELCOFAIR PAYMENT RECEIPT',
      prefix: settings.payments?.receipt?.prefix || 'TF-RCPT-',
      accentColor: settings.payments?.receipt?.accentColor || '#2E6F8E',
      logoUrl: settings.payments?.receipt?.logoUrl || '',
      footer: settings.payments?.receipt?.footer || 'Thank you for your payment.',
      terms: settings.payments?.receipt?.terms || 'This receipt confirms a payment recorded and approved by TELCOFAIR.',
      showCompanyLogo: settings.payments?.receipt?.showCompanyLogo !== false,
      showPaymentMethod: settings.payments?.receipt?.showPaymentMethod !== false,
      showPackage: settings.payments?.receipt?.showPackage !== false,
      emailOnApproval: settings.payments?.receipt?.emailOnApproval !== false,
    },
  },
  storage: {
    provider: settings.storage?.provider || 'firebase',
    firebase: { basePath: settings.storage?.firebase?.basePath || '' },
    cloudinary: {
      cloudName: settings.storage?.cloudinary?.cloudName || '',
      uploadPreset: settings.storage?.cloudinary?.uploadPreset || '',
      folder: settings.storage?.cloudinary?.folder || '',
    },
  },
  meeting: {
    agoraEnabled: Boolean(settings.agora?.enabled),
    agoraAppId: settings.agora?.appId || '',
    employeeWebRtcEnabled: settings.employeeMeetings?.enabled !== false,
  },
  features: {
    ai: Boolean(settings.ai?.enabled),
    videoMeetings: Boolean(settings.agora?.enabled),
    externalCalendars: Boolean(settings.calendar?.enabled),
    pushNotifications: Boolean(settings.push?.enabled),
    payments: Boolean(settings.payments?.enabled),
    whatsapp: Boolean(settings.whatsapp?.enabled),
  },
  whatsapp: {
    enabled: Boolean(settings.whatsapp?.enabled),
    provider: settings.whatsapp?.provider || 'custom_gateway',
    authEnabled: settings.auth?.provider === 'whatsapp',
    verificationEnabled: settings.auth?.verificationProvider === 'whatsapp',
    requestOtpUrl: settings.whatsapp?.requestOtpUrl || '',
    verifyOtpUrl: settings.whatsapp?.verifyOtpUrl || '',
    gatewayUrl: settings.whatsapp?.gatewayUrl || '',
  },
  updatedAt: serverTimestamp(),
});

async function requireSuperAdmin() {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Super Admin authentication is required.');
  const snap = await getDoc(doc(db, 'users', uid));
  const data = snap.exists() ? snap.data() || {} : {};
  if (!isSuperAdminRole(data.role || data.userRole) || data.isActive === false || data.isBlocked === true) {
    throw new Error('Only Super Admin can change platform configuration.');
  }
  return { uid, data };
}

async function readIntegrationDocs() {
  // Browser code must never read private provider credentials. Only redacted
  // operational settings are available here. Server-side gateways/Functions
  // must own SMTP passwords, API secrets, webhook secrets and certificates.
  await requireSuperAdmin();
  const adminSnap = await getDoc(ADMIN_RUNTIME);
  const adminData = adminSnap?.exists?.() ? adminSnap.data() || {} : {};
  return adminData.settings || {};
}

export async function getPlatformIntegrationSettings() {
  const settings = await readIntegrationDocs();
  return {
    settings,
    secureEndpointAvailable: false,
    storageMode: 'direct_firestore',
    securityWarning: 'Browser storage for private integration secrets is disabled. Configure SMTP/payment/API/Agora/calendar secrets only in a secured server-side gateway or Firebase Function before enabling those integrations in production.',
  };
}

export async function savePlatformIntegrationSettings(settings) {
  const actor = await requireSuperAdmin();
  const clean = stripUndefined(settings || {});
  if (clean.registration?.paymentRequired && clean.payments?.enabled === false) {
    throw new Error('Enable the payment system before requiring payment during registration.');
  }
  if (clean.payments?.manual?.upi?.enabled) {
    if (!String(clean.payments.manual.upi.upiId || '').trim()) throw new Error('UPI ID is required when UPI payment is enabled.');
    if (!String(clean.payments.manual.upi.payeeName || '').trim()) throw new Error('Payee / business name is required when UPI payment is enabled.');
  }
  if (clean.payments?.manual?.bankTransfer?.enabled) {
    const bank = clean.payments.manual.bankTransfer || {};
    if (!String(bank.accountHolder || '').trim() || !String(bank.bankName || '').trim() || !String(bank.accountNumber || '').trim()) {
      throw new Error('Account holder, bank name and account number are required when Bank Transfer is enabled.');
    }
  }
  const receiptPrefix = String(clean.payments?.receipt?.prefix || 'TF-RCPT-').trim();
  if (!/^[A-Za-z0-9_-]{2,20}$/.test(receiptPrefix)) throw new Error('Receipt prefix may contain only letters, numbers, hyphens and underscores (2–20 characters).');
  const receiptAccent = String(clean.payments?.receipt?.accentColor || '#2E6F8E').trim();
  if (!/^#[0-9A-F]{6}$/i.test(receiptAccent)) throw new Error('Receipt accent color must be a six-digit hex color such as #2E6F8E.');
  const publicSettings = publicProjection(clean);
  const adminSafeSettings = omitSensitiveSettings(clean);
  await Promise.all([
    // Replace, rather than merge, so secrets stored by older builds are actively
    // removed from browser-readable runtime documents on the next save.
    setDoc(ADMIN_RUNTIME, { settings: adminSafeSettings, updatedAt: serverTimestamp(), updatedBy: actor.uid, source: 'direct_firestore_redacted' }),
    // Previous browser builds wrote private credentials to Firestore. Purge that
    // document; private credentials must now live exclusively in a backend secret store.
    deleteDoc(SECRET_DOC).catch(() => {}),
    setDoc(PUBLIC_RUNTIME, publicSettings, { merge: true }),
    setDoc(PLATFORM_CONFIG, {
      registrationIntegrations: {
        authenticationProvider: clean.auth?.provider || 'firebase_auth',
        verificationProvider: clean.auth?.verificationProvider || 'firebase_auth',
        passwordResetProvider: clean.auth?.passwordResetProvider || 'firebase_auth',
        paymentRequired: Boolean(clean.registration?.paymentRequired),
        paymentEnabled: Boolean(clean.payments?.enabled),
        paymentProvider: clean.payments?.provider || 'stripe',
        emailEnabled: Boolean(clean.email?.enabled),
        emailProvider: clean.email?.provider || 'smtp',
        smsEnabled: Boolean(clean.sms?.enabled),
        smsProvider: clean.sms?.provider || 'twilio',
        whatsappEnabled: Boolean(clean.whatsapp?.enabled),
        whatsappProvider: clean.whatsapp?.provider || 'custom_gateway',
        notificationChannels: clean.notifications || {},
        remindersEnabled: clean.registrationReminders?.enabled !== false,
        reminderFirstMinutes: Number(clean.registrationReminders?.firstReminderMinutes || 60),
        reminderRepeatHours: Number(clean.registrationReminders?.repeatHours || 48),
        reminderMaxCount: Number(clean.registrationReminders?.maxReminders || 8),
        reminderExpiryDays: Number(clean.registrationReminders?.expiryDays || 30),
      },
      updatedAt: serverTimestamp(),
      updatedBy: actor.uid,
    }, { merge: true }),
  ]);
  return { settings: clean, directFirestore: true };
}

const renderTemplate = (text = '', variables = {}) => String(text || '').replace(/{{\s*([\w.]+)\s*}}/g, (_, key) => {
  const value = key.split('.').reduce((obj, part) => obj?.[part], variables);
  return value == null ? '' : String(value);
});

export async function getEmailTemplates() {
  await requireSuperAdmin();
  const snap = await getDocs(collection(db, TEMPLATE_COLLECTION));
  const saved = new Map(snap.docs.map((d) => [d.id, { key: d.id, ...d.data() }]));
  const templates = Object.entries(DEFAULT_TEMPLATES).map(([key, fallback]) => ({ key, enabled: true, ...fallback, ...(saved.get(key) || {}) }));
  saved.forEach((value, key) => {
    if (!DEFAULT_TEMPLATES[key]) templates.push({ key, ...value });
  });
  templates.sort((a, b) => String(a.name || a.key).localeCompare(String(b.name || b.key)));
  return { templates };
}

export async function saveEmailTemplate(templateKey, template) {
  const actor = await requireSuperAdmin();
  if (!templateKey) throw new Error('Template key is required.');
  const payload = { ...stripUndefined(template || {}), updatedAt: serverTimestamp(), updatedBy: actor.uid };
  await setDoc(doc(db, TEMPLATE_COLLECTION, templateKey), payload, { merge: true });
  return { success: true, template: { key: templateKey, ...payload } };
}

export async function resetEmailTemplate(templateKey) {
  await requireSuperAdmin();
  if (!DEFAULT_TEMPLATES[templateKey]) {
    await deleteDoc(doc(db, TEMPLATE_COLLECTION, templateKey));
    return { success: true, template: { key: templateKey, enabled: true, subject: '', html: '', text: '' } };
  }
  await deleteDoc(doc(db, TEMPLATE_COLLECTION, templateKey)).catch(() => {});
  return { success: true, template: { key: templateKey, enabled: true, ...DEFAULT_TEMPLATES[templateKey] } };
}

async function postPublicGateway(endpoint, payload, fallbackMessage) {
  const url = String(endpoint || '').trim();
  if (!url) throw new Error('The configured public gateway is unavailable.');
  if (!/^https:\/\//i.test(url) && !/^http:\/\/localhost(?::\d+)?\//i.test(url)) {
    throw new Error('Gateway URL must use HTTPS.');
  }
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload || {}),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body?.success === false || body?.ok === false) {
    throw new Error(body?.message || fallbackMessage || `Gateway returned HTTP ${response.status}.`);
  }
  return body;
}

async function sendThroughHttpGateway(channel, payload) {
  const settings = await readIntegrationDocs();
  const channelSettings = channel === 'whatsapp' ? settings.whatsapp : channel === 'sms' ? settings.sms?.custom : settings.email;
  const endpoint = channelSettings?.gatewayUrl || channelSettings?.endpoint || channelSettings?.httpGateway?.endpoint || '';
  if (!endpoint) throw new Error(`${channel === 'email' ? 'Email' : channel === 'sms' ? 'SMS' : 'WhatsApp'} HTTP gateway is not configured.`);
  if (!/^https:\/\//i.test(endpoint) && !/^http:\/\/localhost(?::\d+)?\//i.test(endpoint)) throw new Error('Gateway URL must use HTTPS (localhost HTTP is allowed for development).');
  const token = channelSettings?.apiToken || channelSettings?.token || channelSettings?.httpGateway?.token || '';
  const authHeader = channelSettings?.authHeader || channelSettings?.httpGateway?.authHeader || 'Authorization';
  const authScheme = channelSettings?.authScheme ?? channelSettings?.httpGateway?.authScheme ?? 'Bearer';
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers[authHeader] = authScheme ? `${authScheme} ${token}`.trim() : token;
  const normalizedPayload = { ...payload };
  if (normalizedPayload.operation && !normalizedPayload.action) normalizedPayload.action = normalizedPayload.operation;
  if (normalizedPayload.action && !normalizedPayload.operation) normalizedPayload.operation = normalizedPayload.action;
  const response = await fetch(endpoint, { method: 'POST', headers, body: JSON.stringify({ channel, ...normalizedPayload }) });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body?.success === false || body?.ok === false) throw new Error(body?.message || `Gateway returned HTTP ${response.status}.`);
  return body;
}

export async function sendEmailTemplateTest(templateKey, to, variables = {}) {
  const { templates } = await getEmailTemplates();
  const template = templates.find((row) => row.key === templateKey);
  if (!template || template.enabled === false) throw new Error('This email template is disabled.');
  return sendThroughHttpGateway('email', {
    to,
    subject: renderTemplate(template.subject, variables),
    html: renderTemplate(template.html, variables),
    text: renderTemplate(template.text, variables),
    templateKey,
  });
}

export async function sendPaymentApprovalEmail({ payment = {}, receiptConfig = {}, attachment = null } = {}) {
  const to = String(payment.email || '').trim().toLowerCase();
  if (!to) throw new Error('Payment email address is missing.');
  const { templates } = await getEmailTemplates();
  const storedTemplate = templates.find((row) => row.key === 'payment_approved_receipt');
  const template = storedTemplate && storedTemplate.enabled !== false ? storedTemplate : { key: 'payment_approved_receipt', enabled: true, ...DEFAULT_TEMPLATES.payment_approved_receipt };
  const variables = {
    firstName: payment.firstName || String(payment.userName || '').split(' ')[0] || 'Customer',
    fullName: payment.userName || '',
    email: to,
    companyName: payment.companyName || '',
    amount: `${payment.currency || 'EUR'} ${Number(payment.amount || 0).toFixed(2)}`,
    planName: payment.packageName || payment.planName || 'TELCOFAIR subscription',
    receiptNumber: payment.receiptNumber || '',
    transactionReference: payment.transactionReference || '',
    paymentMethod: String(payment.paymentMethod || '').replace(/_/g, ' '),
    message: receiptConfig.footer || 'Thank you for your payment.',
    platformName: 'TELCOFAIR',
  };
  const payload = {
    to,
    subject: renderTemplate(template.subject, variables),
    html: renderTemplate(template.html, variables),
    text: renderTemplate(template.text, variables),
    templateKey: 'payment_approved_receipt',
    operation: 'payment_approved_receipt',
    receiptNumber: payment.receiptNumber || '',
  };
  if (attachment?.contentBase64) payload.attachments = [attachment];
  return sendThroughHttpGateway('email', payload);
}

export async function sendPaymentRejectedEmail({ payment = {}, reason = '' } = {}) {
  const to = String(payment.email || '').trim().toLowerCase();
  if (!to) return { skipped: true, reason: 'missing_email' };
  const { templates } = await getEmailTemplates();
  const template = templates.find((row) => row.key === 'payment_rejected');
  if (!template || template.enabled === false) return { skipped: true, reason: 'template_disabled' };
  const variables = {
    firstName: payment.firstName || String(payment.userName || '').split(' ')[0] || 'Customer',
    companyName: payment.companyName || '', email: to,
    amount: `${payment.currency || 'EUR'} ${Number(payment.amount || 0).toFixed(2)}`,
    planName: payment.packageName || payment.planName || 'TELCOFAIR subscription',
    message: reason || 'The submitted payment details could not be verified.',
  };
  return sendThroughHttpGateway('email', {
    to,
    subject: renderTemplate(template.subject, variables),
    html: renderTemplate(template.html, variables),
    text: renderTemplate(template.text, variables),
    templateKey: 'payment_rejected', operation: 'payment_rejected',
  });
}

export async function testPlatformIntegration(type, payload = {}) {
  const settings = await readIntegrationDocs();
  switch (type) {
    case 'storage': {
      if (settings.storage?.provider === 'cloudinary' && (!settings.storage?.cloudinary?.cloudName || !settings.storage?.cloudinary?.uploadPreset)) {
        throw new Error('Cloudinary Cloud Name and unsigned Upload Preset are required.');
      }
      return { success: true, mode: 'configuration' };
    }
    case 'email': {
      if (settings.email?.provider === 'smtp' && (!settings.email?.smtp?.host || !settings.email?.smtp?.user)) throw new Error('SMTP host and username are required.');
      if (payload?.to) return sendThroughHttpGateway('email', { to: payload.to, subject: 'TELCOFAIR email test', text: 'TELCOFAIR email gateway test.' });
      return { success: true, mode: settings.email?.gatewayUrl ? 'gateway' : 'configuration' };
    }
    case 'sms':
      if (!payload?.phone) throw new Error('Enter a test phone number.');
      if (settings.sms?.provider === 'custom') return sendThroughHttpGateway('sms', { phone: payload.phone, message: payload.message || 'TELCOFAIR SMS test.' });
      return { success: true, mode: 'configuration', note: 'Provider configuration saved; use a custom HTTPS gateway for no-Functions live delivery.' };
    case 'whatsapp':
      if (!payload?.phone) throw new Error('Enter a test WhatsApp number.');
      return sendThroughHttpGateway('whatsapp', { phone: payload.phone, message: payload.message || 'TELCOFAIR WhatsApp test.' });
    case 'ai':
      if (!settings.ai?.enabled) throw new Error('AI is disabled.');
      if (!settings.ai?.openai?.apiKey) throw new Error('OpenAI API key is not configured.');
      return { success: true, mode: 'configuration', note: 'Key is stored for the Super Admin browser. Live OpenAI calls should use an external secure gateway.' };
    case 'agora':
      if (!settings.agora?.appId) throw new Error('Agora App ID is required.');
      return { success: true, mode: 'configuration' };
    case 'calendar':
      if (!settings.calendar?.enabled) throw new Error('External calendars are disabled.');
      return { success: true, mode: 'configuration' };
    default:
      return { success: true, mode: 'configuration' };
  }
}

export async function sendProfessionalVerificationEmail(uid) {
  const userSnap = await getDoc(doc(db, 'users', uid));
  if (!userSnap.exists()) throw new Error('User was not found.');
  const user = userSnap.data() || {};
  const settings = await readIntegrationDocs();
  if (settings.auth?.verificationProvider === 'firebase_auth') {
    if (auth.currentUser?.uid !== uid) return { success: false, provider: 'firebase_auth', requiresUserSession: true };
    await sendEmailVerification(auth.currentUser);
    return { success: true, provider: 'firebase_auth' };
  }
  if (settings.auth?.verificationProvider === 'manual') {
    return { success: false, provider: 'manual', manualVerificationRequired: true };
  }
  if (settings.auth?.verificationProvider === 'whatsapp') {
    return sendThroughHttpGateway('whatsapp', { phone: user.phone || '', operation: 'verification', uid, name: user.displayName || user.firstName || '' });
  }
  const appUrl = settings.email?.verificationBaseUrl || window.location.origin;
  return sendThroughHttpGateway('email', {
    to: user.email,
    operation: 'send_verification',
    uid,
    appUrl,
    variables: { firstName: user.firstName || '', fullName: user.displayName || '' },
  });
}

export async function verifyProfessionalEmailToken(token) {
  if (!token) throw new Error('Verification token is missing.');
  const runtime = await getPublicPlatformRuntime();
  const endpoint = runtime.email?.verificationGatewayUrl || runtime.email?.gatewayUrl || '';
  return postPublicGateway(endpoint, { channel: 'email', action: 'verify_token', operation: 'verify_token', token }, 'Unable to verify this email link.');
}

export async function manualVerifyUserEmail(uid, verified = true) {
  const actor = await requireSuperAdmin();
  const runtime = await getPublicPlatformRuntime();
  if ((runtime.auth?.verificationProvider || 'firebase_auth') === 'firebase_auth') {
    throw new Error('Firebase Authentication is the authoritative email-verification provider. A browser administrator cannot manually override Firebase emailVerified; resend verification from the user session or use a secured Admin SDK backend.');
  }
  await setDoc(doc(db, 'users', uid), {
    isVerified: Boolean(verified),
    emailVerified: Boolean(verified),
    verificationOverride: Boolean(verified),
    verificationMethod: verified ? 'manual' : null,
    verifiedAt: verified ? serverTimestamp() : null,
    verifiedBy: verified ? actor.uid : null,
    updatedAt: serverTimestamp(),
  }, { merge: true });
  return { success: true, firestoreOverride: true };
}

export async function createPlatformAdmin(data = {}) {
  await requireSuperAdmin();
  const email = String(data.email || '').trim().toLowerCase();
  const password = String(data.password || '');
  if (!email || !password) throw new Error('Email and password are required.');
  if (password.length < 8) throw new Error('Temporary password must contain at least 8 characters.');
  const settings = await readIntegrationDocs();
  const verificationProvider = settings.auth?.verificationProvider || 'firebase_auth';
  const secondary = initializeApp(app.options, `admin-create-${Date.now()}`);
  const secondaryAuth = getAuth(secondary);
  let createdUser = null;
  try {
    const credential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
    createdUser = credential.user;
    const uid = createdUser.uid;

    // Persist the admin profile before attempting verification delivery so a
    // gateway failure never leaves an invisible Auth-only administrator.
    try {
      await setDoc(doc(db, 'users', uid), {
        uid,
        userId: uid,
        email,
        emailNormalized: email,
        displayName: data.displayName || '',
        phone: data.phone || data.phoneNumber || '',
        phoneNumber: data.phoneNumber || data.phone || '',
        role: 'super_admin',
        userRole: 'super_admin',
        isActive: data.isActive !== false,
        isBlocked: false,
        isVerified: false,
        emailVerified: false,
        verificationMethod: verificationProvider,
        verificationDeliveryStatus: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (profileError) {
      await deleteUser(createdUser).catch(() => {});
      throw profileError;
    }

    let verificationWarning = '';
    try {
      if (verificationProvider === 'firebase_auth') {
        await sendEmailVerification(createdUser);
      } else if (verificationProvider === 'professional_email') {
        await sendThroughHttpGateway('email', {
          to: email,
          operation: 'send_verification',
          uid,
          appUrl: settings.email?.verificationBaseUrl || window.location.origin,
          variables: { firstName: data.displayName || '' },
        });
      } else if (verificationProvider === 'whatsapp') {
        const phone = data.phone || data.phoneNumber || '';
        if (!phone) throw new Error('A phone number is required when WhatsApp verification is selected.');
        await sendThroughHttpGateway('whatsapp', { phone, operation: 'verification', uid, name: data.displayName || '' });
      }
      await setDoc(doc(db, 'users', uid), {
        verificationDeliveryStatus: verificationProvider === 'manual' ? 'manual_pending' : 'sent',
        verificationDeliveryError: null,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (deliveryError) {
      verificationWarning = friendlyError(deliveryError, 'The administrator was created, but the verification message could not be delivered.');
      await setDoc(doc(db, 'users', uid), {
        verificationDeliveryStatus: 'failed',
        verificationDeliveryError: verificationWarning,
        updatedAt: serverTimestamp(),
      }, { merge: true }).catch(() => {});
    }

    await firebaseSignOut(secondaryAuth).catch(() => {});
    return { success: true, uid, verificationProvider, verificationWarning };
  } finally {
    await firebaseSignOut(secondaryAuth).catch(() => {});
    await deleteApp(secondary).catch(() => {});
  }
}

export async function sendProfessionalPasswordResetEmail(email) {
  const runtime = await getPublicPlatformRuntime();
  const provider = runtime.auth?.passwordResetProvider || 'firebase_auth';
  if (provider === 'firebase_auth') {
    await sendPasswordResetEmail(auth, String(email || '').trim().toLowerCase());
    return { success: true, provider: 'firebase_auth' };
  }
  if (provider === 'whatsapp') {
    throw new Error('WhatsApp password reset requires the external WhatsApp authentication gateway to issue a reset challenge.');
  }
  const endpoint = runtime.email?.passwordResetGatewayUrl || runtime.email?.gatewayUrl || '';
  return postPublicGateway(endpoint, { channel: 'email', to: String(email || '').trim().toLowerCase(), action: 'password_reset_request', operation: 'password_reset_request' }, 'Unable to send the password reset email.');
}

export const requestProfessionalPasswordResetEmail = sendProfessionalPasswordResetEmail;

const jsDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export async function processPendingRegistrationReminders({ dryRun = false, limit = 100 } = {}) {
  await requireSuperAdmin();
  const settings = await readIntegrationDocs();
  const cfg = settings.registrationReminders || {};
  if (cfg.enabled === false) return { success: true, disabled: true, eligible: 0, sent: 0, skipped: 0, failures: [] };
  const firstMinutes = Math.max(0, Number(cfg.firstReminderMinutes || 60));
  const repeatHours = Math.max(1, Number(cfg.repeatHours || 48));
  const maxReminders = Math.max(1, Number(cfg.maxReminders || 8));
  const expiryDays = Math.max(1, Number(cfg.expiryDays || 30));
  const now = new Date();
  const usersSnap = await getDocs(collection(db, 'users'));
  const candidates = usersSnap.docs
    .map((row) => ({ id: row.id, ...row.data() }))
    .filter((user) => {
      if (user.registrationIncomplete === true || user.profileCompleted === false) return true;
      const stage = String(user.registrationStage || user.stage || '').toLowerCase();
      const status = String(user.registrationStatus || '').toLowerCase();
      if (stage && !['completed', 'active', 'done'].includes(stage)) return true;
      return status.includes('pending') || ['expired', 'incomplete'].includes(status);
    })
    .slice(0, Math.max(1, Number(limit || 100)));
  const summary = { success: true, dryRun, eligible: 0, sent: 0, skipped: 0, expired: 0, failures: [] };

  for (const user of candidates) {
    const completed = user.registrationIncomplete === false && user.profileCompleted === true;
    if (completed) { summary.skipped++; continue; }
    const count = Number(user.registrationReminderCount || 0);
    if (count >= maxReminders) { summary.skipped++; continue; }
    const created = jsDate(user.createdAt || user.registrationStartedAt || user.updatedAt) || now;
    const ageMs = now.getTime() - created.getTime();
    if (ageMs < firstMinutes * 60000) { summary.skipped++; continue; }
    if (ageMs > expiryDays * 86400000) {
      summary.expired++;
      if (!dryRun) await setDoc(doc(db, 'users', user.id), { registrationReminderExpiredAt: serverTimestamp(), updatedAt: serverTimestamp() }, { merge: true });
      continue;
    }
    const last = jsDate(user.registrationReminderLastAt);
    if (last && now.getTime() - last.getTime() < repeatHours * 3600000) { summary.skipped++; continue; }

    const stage = String(user.registrationStage || user.stage || 'registration').toLowerCase();
    if (stage.includes('verification') && cfg.verificationReminderEnabled === false) { summary.skipped++; continue; }
    if ((stage.includes('payment') || stage.includes('subscription')) && cfg.paymentReminderEnabled === false) { summary.skipped++; continue; }
    if (!stage.includes('verification') && !stage.includes('payment') && !stage.includes('subscription') && cfg.profileReminderEnabled === false) { summary.skipped++; continue; }

    summary.eligible++;
    if (dryRun) continue;
    try {
      const variables = {
        firstName: user.firstName || 'there',
        fullName: user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        email: user.email || '',
        companyName: user.companyName || '',
        registrationLink: `${String(settings.email?.verificationBaseUrl || window.location.origin).replace(/\/$/, '')}/login?continue=register`,
        registrationStage: stage,
      };
      const deliveries = [];
      if (settings.notifications?.email && user.email) deliveries.push(sendEmailTemplateTest('registration_completion', user.email, variables));
      if (settings.notifications?.whatsapp && (user.phone || user.phoneNumber)) {
        deliveries.push(sendThroughHttpGateway('whatsapp', {
          phone: user.phone || user.phoneNumber,
          operation: 'registration_completion',
          message: `Complete your TELCOFAIR registration: ${variables.registrationLink}`,
          variables,
        }));
      }
      if (settings.notifications?.inApp !== false) {
        deliveries.push(setDoc(doc(collection(db, 'notifications')), {
          recipientId: user.id,
          title: 'Complete your TELCOFAIR registration',
          message: 'Your registration is still incomplete. Continue from your saved registration stage.',
          type: 'registration_reminder',
          isRead: false,
          createdAt: serverTimestamp(),
        }));
      }
      if (!deliveries.length) throw new Error('No enabled reminder delivery channel is configured.');
      await Promise.all(deliveries);
      await setDoc(doc(db, 'users', user.id), {
        registrationReminderCount: count + 1,
        registrationReminderFirstAt: user.registrationReminderFirstAt || serverTimestamp(),
        registrationReminderLastAt: serverTimestamp(),
        registrationReminderNextEligibleAt: new Date(now.getTime() + repeatHours * 3600000),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      summary.sent++;
    } catch (error) {
      summary.failures.push({ userId: user.id, email: user.email || '', message: friendlyError(error, 'Reminder delivery failed.') });
    }
  }
  return summary;
}

export async function sendPlatformNotification(payload = {}) {
  const settings = await readIntegrationDocs();
  const recipients = Array.isArray(payload.recipients) ? payload.recipients : payload.to ? [payload.to] : [];
  const results = [];
  if (settings.notifications?.inApp !== false) {
    for (const recipient of recipients) {
      await setDoc(doc(collection(db, 'notifications')), {
        recipientId: recipient.uid || recipient.id || recipient,
        title: payload.title || 'TELCOFAIR',
        message: payload.message || '',
        type: payload.type || 'system',
        isRead: false,
        createdAt: serverTimestamp(),
      });
    }
    results.push({ channel: 'in_app', success: true });
  }
  if (settings.notifications?.email && payload.email) {
    results.push({ channel: 'email', ...(await sendThroughHttpGateway('email', payload.email)) });
  }
  if (settings.notifications?.sms && payload.sms) {
    results.push({ channel: 'sms', ...(await sendThroughHttpGateway('sms', payload.sms)) });
  }
  if (settings.notifications?.whatsapp && payload.whatsapp) {
    results.push({ channel: 'whatsapp', ...(await sendThroughHttpGateway('whatsapp', payload.whatsapp)) });
  }
  return { success: true, results };
}

export async function reviewAdvertisement(adId, status, reviewReason = '') {
  const actor = await requireSuperAdmin();
  await setDoc(doc(db, 'ads', adId), { status, reviewReason, moderatedBy: actor.uid, moderatedAt: serverTimestamp(), updatedAt: serverTimestamp() }, { merge: true });
  return { success: true };
}

export async function deleteAdvertisement(adId) {
  await requireSuperAdmin();
  await deleteDoc(doc(db, 'ads', adId));
  return { success: true };
}

export async function getPublicPlatformRuntime() {
  const snap = await getDoc(PUBLIC_RUNTIME);
  return snap.exists() ? snap.data() || {} : {};
}

export async function getWhatsAppAuthSettings() {
  const runtime = await getPublicPlatformRuntime();
  return runtime.whatsapp || {};
}

export async function requestWhatsAppOtp(phone, purpose = 'verification') {
  const runtime = await getPublicPlatformRuntime();
  const endpoint = runtime.whatsapp?.requestOtpUrl || runtime.whatsapp?.gatewayUrl || '';
  return postPublicGateway(endpoint, { channel: 'whatsapp', action: 'request_otp', operation: 'request_otp', phone, purpose }, 'Unable to request the WhatsApp OTP.');
}

export async function verifyWhatsAppOtp(phone, otp, purpose = 'verification') {
  const runtime = await getPublicPlatformRuntime();
  const endpoint = runtime.whatsapp?.verifyOtpUrl || runtime.whatsapp?.gatewayUrl || '';
  return postPublicGateway(endpoint, { channel: 'whatsapp', action: 'verify_otp', operation: 'verify_otp', phone, otp, purpose }, 'Unable to verify the WhatsApp OTP.');
}

export { renderTemplate, DEFAULT_TEMPLATES, friendlyError };

export default {
  getPlatformIntegrationSettings,
  savePlatformIntegrationSettings,
  testPlatformIntegration,
  sendProfessionalVerificationEmail,
  verifyProfessionalEmailToken,
  manualVerifyUserEmail,
  createPlatformAdmin,
  sendProfessionalPasswordResetEmail,
  requestProfessionalPasswordResetEmail,
  sendPlatformNotification,
  processPendingRegistrationReminders,
  reviewAdvertisement,
  deleteAdvertisement,
  getEmailTemplates,
  saveEmailTemplate,
  resetEmailTemplate,
  sendEmailTemplateTest,
  sendPaymentApprovalEmail,
  sendPaymentRejectedEmail,
  getPublicPlatformRuntime,
  getWhatsAppAuthSettings,
  requestWhatsAppOtp,
  verifyWhatsAppOtp,
};
